import { Info } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import {
  getPeriodText,
  useCommonMetrics
} from "@/components/layouts/LeadAnalysis/helpers";
import { LeadAnalysisKPI } from "@/components/layouts/LeadAnalysis/types";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Trend } from "@/components/ui/trend";
import {
  LeadAnalysisData,
  SettingsPeriod,
  TrendType
} from "@/entities/reporting/types";
import { cn } from "@/utils/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { LineChart, Line, ResponsiveContainer } from "recharts";

interface DataPoint {
  value: number;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  data: DataPoint[];
  onClick: () => void;
  isSelected: boolean;
  info?: string;
  period: SettingsPeriod;
  trend: TrendType;
  isPercentage?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  data,
  onClick,
  isSelected,
  info,
  period,
  trend,
  isPercentage = false
}) => {
  const { t } = useTranslation();

  const periodText = getPeriodText(period, t);
  return (
    <div
      className={cn(
        "rounded-lg pt-3 pl-4 pr-8 pb-4 flex flex-col h-full border-[0.5px] w-full cursor-pointer",
        isSelected ? "border-border-widget" : "border-border-widget-card"
      )}
      style={{
        background: isSelected
          ? `linear-gradient(217deg, rgba(12, 17, 29, 0.00) -2.61%, var(--opacity-black-80, rgba(12, 17, 29, 0.80)) 104.29%), var(--surface-widget-card, #161E2C)`
          : `
          linear-gradient(198deg, var(--opacity-white-8, rgba(255, 255, 255, 0.08)) -54.24%, rgba(255, 255, 255, 0.02) 91.6%), var(--surface-widget-card, #161E2C)
        `
      }}
      onClick={onClick}
    >
      <div className="flex flex-row mb-2 justify-between">
        <Text variant="bodySecondary">{title}</Text>
        {info && (
          <Tooltip>
            <TooltipTrigger asChild>
              <IconContainer
                size="2"
                color="border-field-default"
                nativeStroke
                className="block"
              >
                <Info />
              </IconContainer>
            </TooltipTrigger>
            <TooltipContent maxWidth={24}>{info}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex flex-row items-center gap-2">
        <div className="flex items-baseline">
          <Text as="div" variant="title1H1">
            {Math.round(Number(value) * 10) / 10}
          </Text>
          {isPercentage && (
            <Text as="div" variant="title3H1" className="font-normal ml-[2px]">
              %
            </Text>
          )}
        </div>
        <div className="w-[80%] h-12 mt-2 flex flex-row items-center">
          <ResponsiveContainer width="80%" height="100%">
            <LineChart data={data} style={{ cursor: "pointer" }}>
              <defs>
                <linearGradient
                  id="lineGradient"
                  x1="136.142"
                  y1="2.52484"
                  x2="18.4229"
                  y2="65.7964"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#F2F4F7" />
                  <stop offset="100%" stopColor="white" stopOpacity="0.04" />
                </linearGradient>
              </defs>
              <Line
                type="monotone"
                dataKey="value"
                stroke="url(#lineGradient)"
                strokeWidth={1.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
          {trend.comparedToValue !== 0 &&
            trend.comparedToValue !== -1 &&
            trend.valueForPeriod !== 0 && (
              <div className="flex flex-col">
                <Trend value={trend} />
                <Text
                  as="div"
                  variant="smBodyTertiary"
                  className="ml-1 whitespace-nowrap"
                >
                  {periodText}
                </Text>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

interface MetricsDashboardProps {
  selectedKpi: LeadAnalysisKPI;
  setSelectedKpi: React.Dispatch<React.SetStateAction<LeadAnalysisKPI>>;
  data: LeadAnalysisData;
  period: SettingsPeriod;
}

const MetricsDashboard: React.FC<MetricsDashboardProps> = ({
  selectedKpi,
  setSelectedKpi,
  data,
  period
}) => {
  const { t } = useTranslation();

  const { avgLeadsData, qualificationData, conversionData } =
    useCommonMetrics(data);

  return (
    <div className="w-full flex flex-row grow gap-4 items-start self-stretch">
      <MetricCard
        title={t(`leadAnalysis.kpi.avgNewLeads`)}
        value={
          data.leadsAnalysis.avgMonthlyQualifiedConvertedLeads.overall.value
        }
        data={avgLeadsData}
        isSelected={
          selectedKpi === LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads
        }
        onClick={() =>
          setSelectedKpi(LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads)
        }
        period={period}
        trend={
          data.leadsAnalysis.avgMonthlyQualifiedConvertedLeads.overall.trend
        }
      />
      <MetricCard
        title={t(`leadAnalysis.kpi.leadQualification`)}
        value={data.leadsAnalysis.leadQualificationRate.overall.value}
        data={qualificationData}
        isSelected={selectedKpi === LeadAnalysisKPI.LeadQualificationRate}
        onClick={() => setSelectedKpi(LeadAnalysisKPI.LeadQualificationRate)}
        info={t("leadAnalysis.kpi.leadQualificationInfo")}
        period={period}
        trend={data.leadsAnalysis.leadQualificationRate.overall.trend}
        isPercentage
      />
      <MetricCard
        title={t(`leadAnalysis.kpi.leadConversion`)}
        value={data.leadsAnalysis.leadConversionRate.overall.value}
        data={conversionData}
        isSelected={selectedKpi === LeadAnalysisKPI.LeadConversionRate}
        onClick={() => setSelectedKpi(LeadAnalysisKPI.LeadConversionRate)}
        info={t("leadAnalysis.kpi.leadConversionInfo")}
        period={period}
        trend={data.leadsAnalysis.leadConversionRate.overall.trend}
        isPercentage
      />
    </div>
  );
};

export default MetricsDashboard;
